/**
 * MentorProfileSetup.jsx
 *
 * Shown to approved mentors who haven't completed their public profile yet.
 * Reached via: Login → "Continue as Mentor" → profile incomplete → /mentor-profile-setup
 *
 * Tabs: Photo & Bio | Expertise & Price | Availability | Bank Details
 * On save → navigates to /mentordashboard
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { supabase } from "../supabase";

const CATEGORIES = [
  { value: "mental_health",   label: "Mental Health",    icon: "🧠" },
  { value: "career",          label: "Career Guidance",  icon: "💼" },
  { value: "relationships",   label: "Relationships",    icon: "💑" },
  { value: "stress",          label: "Stress & Anxiety", icon: "🌊" },
  { value: "grief",           label: "Grief & Loss",     icon: "🕊️" },
  { value: "addiction",       label: "Addiction Support",icon: "🤝" },
  { value: "parenting",       label: "Parenting",        icon: "👨‍👩‍👧" },
  { value: "student",         label: "Student Wellness", icon: "📚" },
  { value: "general",         label: "General Wellness", icon: "🌿" },
];

const DAYS  = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const TIMES = ["06:00","07:00","08:00","09:00","10:00","11:00","12:00",
               "13:00","14:00","15:00","16:00","17:00","18:00","19:00",
               "20:00","21:00","22:00"];
const TABS  = ["Photo & Bio","Expertise & Price","Availability","Bank Details"];

const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  .mps-root{font-family:'DM Sans',sans-serif;min-height:100vh;background:linear-gradient(135deg,#f0f4ff 0%,#fef6f0 100%);padding-bottom:80px;-webkit-font-smoothing:antialiased}
  .mps-hero{background:linear-gradient(135deg,#3949ab 0%,#5c6bc0 60%,#c95b3d 100%);padding:28px 16px 40px;text-align:center;color:#fff}
  .mps-hero h1{font-family:'DM Serif Display',serif;font-size:clamp(1.4rem,4vw,2rem);font-weight:400;margin-bottom:6px}
  .mps-hero p{font-size:.88rem;opacity:.85}
  .mps-tabs{background:#fff;border-bottom:1px solid #e8eaf6;display:flex;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none;position:sticky;top:0;z-index:50}
  .mps-tabs::-webkit-scrollbar{display:none}
  .mps-tab{padding:13px 18px;border:none;background:none;font-family:'DM Sans',sans-serif;font-size:.83rem;font-weight:600;color:#888;cursor:pointer;border-bottom:2.5px solid transparent;white-space:nowrap;transition:all .15s;flex-shrink:0}
  .mps-tab.active{color:#5c6bc0;border-bottom-color:#5c6bc0}
  .mps-card{max-width:640px;margin:24px auto;background:#fff;border-radius:20px;box-shadow:0 4px 24px rgba(92,107,192,.1);padding:24px 20px}
  @media(min-width:600px){.mps-card{padding:32px 36px}}
  .mps-card-title{font-family:'DM Serif Display',serif;font-size:1.3rem;color:#1a1a2e;margin-bottom:4px}
  .mps-card-sub{font-size:.85rem;color:#888;margin-bottom:22px;line-height:1.6}
  .mps-grid{display:grid;grid-template-columns:1fr;gap:16px}
  @media(min-width:540px){.mps-grid{grid-template-columns:1fr 1fr}}
  .mps-grid .full{grid-column:1/-1}
  .mps-field{display:flex;flex-direction:column;gap:6px}
  .mps-label{font-size:.78rem;font-weight:600;color:#555;text-transform:uppercase;letter-spacing:.04em}
  .mps-label .req{color:#f44336}
  .mps-input,.mps-select,.mps-textarea{padding:10px 13px;border:1.5px solid #e8eaf6;border-radius:10px;font-family:'DM Sans',sans-serif;font-size:.9rem;color:#1a1a2e;background:#f8f9ff;outline:none;transition:border-color .2s,background .2s;width:100%}
  .mps-input:focus,.mps-select:focus,.mps-textarea:focus{border-color:#5c6bc0;background:#fff}
  .mps-input.err,.mps-select.err{border-color:#f44336}
  .mps-err{font-size:.76rem;color:#f44336}
  .mps-hint{font-size:.75rem;color:#aaa;line-height:1.5}
  .mps-photo-wrap{display:flex;align-items:center;gap:16px;margin-bottom:20px;flex-wrap:wrap}
  .mps-photo-preview{width:80px;height:80px;border-radius:50%;object-fit:cover;border:3px solid #e8eaf6;flex-shrink:0}
  .mps-photo-placeholder{width:80px;height:80px;border-radius:50%;background:#e8eaf6;display:flex;align-items:center;justify-content:center;font-size:2rem;flex-shrink:0}
  .mps-photo-btn{padding:9px 18px;background:#e8eaf6;border:none;border-radius:9px;font-size:.85rem;font-weight:600;color:#3949ab;cursor:pointer;position:relative;overflow:hidden}
  .mps-photo-btn input{position:absolute;inset:0;opacity:0;cursor:pointer;width:100%}
  .mps-cat-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
  @media(min-width:480px){.mps-cat-grid{grid-template-columns:repeat(3,1fr)}}
  .mps-cat-item{padding:12px 8px;border:1.5px solid #e8eaf6;border-radius:12px;cursor:pointer;text-align:center;transition:all .15s;background:#fafbff}
  .mps-cat-item:hover{border-color:#5c6bc0}
  .mps-cat-item.selected{border-color:#5c6bc0;background:#e8eaf6}
  .mps-cat-icon{font-size:1.3rem;display:block;margin-bottom:4px}
  .mps-cat-name{font-size:.75rem;font-weight:700;color:#1a1a2e}
  .mps-price-display{text-align:center;font-size:1.5rem;font-weight:700;color:#5c6bc0;margin:6px 0}
  .mps-price-display span{font-size:.82rem;color:#aaa;font-weight:400}
  input[type=range].mps-slider{-webkit-appearance:none;width:100%;height:6px;border-radius:3px;background:linear-gradient(to right,#5c6bc0 var(--val,50%),#e8eaf6 var(--val,50%));outline:none;cursor:pointer}
  input[type=range].mps-slider::-webkit-slider-thumb{-webkit-appearance:none;width:20px;height:20px;border-radius:50%;background:#5c6bc0;box-shadow:0 2px 8px rgba(92,107,192,.4)}
  .mps-price-ticks{display:flex;justify-content:space-between;font-size:.7rem;color:#aaa;margin-top:4px}
  .mps-avail-wrap{overflow-x:auto}
  .mps-avail-table{width:100%;border-collapse:collapse;min-width:340px}
  .mps-avail-table th{font-size:.72rem;font-weight:700;color:#888;text-transform:uppercase;padding:6px 8px;border-bottom:2px solid #e8eaf6;text-align:left}
  .mps-avail-table td{padding:7px 8px;border-bottom:1px solid #f5f5f5;vertical-align:middle}
  .mps-avail-table input[type=checkbox]{width:16px;height:16px;accent-color:#5c6bc0;cursor:pointer}
  .mps-avail-table select{padding:4px 8px;border:1px solid #e8eaf6;border-radius:6px;font-size:.82rem;background:#f8f9ff;outline:none}
  .mps-alert{padding:11px 14px;border-radius:10px;font-size:.855rem;line-height:1.55;margin-bottom:16px;display:flex;align-items:flex-start;gap:9px}
  .mps-alert.error{background:#ffebee;border:1px solid #ffcdd2;color:#c62828}
  .mps-alert.success{background:#f1f8e9;border:1px solid #c8e6c9;color:#2e7d32}
  .mps-alert.info{background:#e8eaf6;border:1px solid #c5cae9;color:#3949ab}
  .mps-save-btn{width:100%;padding:13px;background:linear-gradient(135deg,#5c6bc0,#3949ab);color:#fff;border:none;border-radius:10px;font-family:'DM Sans',sans-serif;font-weight:700;font-size:.95rem;cursor:pointer;margin-top:24px;display:flex;align-items:center;justify-content:center;gap:8px;box-shadow:0 4px 12px rgba(92,107,192,.3);transition:opacity .2s,transform .2s}
  .mps-save-btn:hover:not(:disabled){opacity:.9;transform:translateY(-1px)}
  .mps-save-btn:disabled{opacity:.5;cursor:not-allowed;transform:none}
  .mps-spin{width:16px;height:16px;border:2px solid rgba(255,255,255,.3);border-top-color:#fff;border-radius:50%;animation:mps-spin .6s linear infinite;flex-shrink:0}
  @keyframes mps-spin{to{transform:rotate(360deg)}}
  .mps-progress{display:flex;gap:6px;justify-content:center;margin-bottom:20px}
  .mps-dot{width:8px;height:8px;border-radius:50%;background:#e8eaf6;transition:background .3s}
  .mps-dot.done{background:#5c6bc0}
  @media(max-width:400px){.mps-card{margin:16px 12px;padding:20px 16px}}
`;

export default function MentorProfileSetup() {
  const navigate = useNavigate();

  const [mentor,    setMentor]   = useState(null);
  const [loading,   setLoading]  = useState(true);
  const [saving,    setSaving]   = useState(false);
  const [alert,     setAlert]    = useState(null);
  const [activeTab, setActiveTab]= useState(0);
  const [errs,      setErrs]     = useState({});

  // Tab 0
  const [photoFile,    setPhotoFile]    = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [bio,          setBio]          = useState("");

  // Tab 1
  const [expertise,      setExpertise]      = useState("");
  const [category,       setCategory]       = useState("general");
  const [sessionPrice,   setSessionPrice]   = useState(200);
  const [languages,      setLanguages]      = useState("");
  const [socialLinkedIn, setSocialLinkedIn] = useState("");
  const [socialWebsite,  setSocialWebsite]  = useState("");

  // Tab 2
  const [availability, setAvailability] = useState(
    DAYS.map((day, i) => ({ day, dayNum: i + 1, enabled: i < 5, startTime: "09:00", endTime: "17:00" }))
  );

  // Tab 3
  const [bank, setBank] = useState({ accountName:"", bankName:"", accountNumber:"", confirmAccount:"", ifsc:"" });

  // ── Load mentor data ───────────────────────────────────────
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { navigate("/", { replace: true }); return; }

    const load = async () => {
      const { data, error } = await supabase
        .from("mentors")
        .select("*")
        .eq("email", user.email)
        .single();

      if (error || !data) {
        setAlert({ type: "error", msg: "Mentor profile not found. Please contact support." });
        setLoading(false);
        return;
      }

      setMentor(data);
      setBio(data.bio || "");
      setExpertise(data.expertise || "");
      setCategory(data.category || "general");
      setSessionPrice(data.session_price || 200);
      setLanguages((data.languages || []).join(", "));
      setSocialLinkedIn(data.social_links?.linkedin || "");
      setSocialWebsite(data.social_links?.website   || "");
      setPhotoPreview(data.profile_photo_url || null);
      setBank({
        accountName:    data.bank_account_name   || "",
        bankName:       "",
        accountNumber:  data.bank_account_number || "",
        confirmAccount: data.bank_account_number || "",
        ifsc:           data.bank_ifsc           || "",
      });

      const { data: avail } = await supabase
        .from("mentor_availability")
        .select("*")
        .eq("mentor_id", data.id);

      if (avail?.length) {
        setAvailability(DAYS.map((day, i) => {
          const ex = avail.find(a => a.day_of_week === i + 1);
          return { day, dayNum: i + 1, enabled: !!ex, startTime: ex?.start_time?.slice(0,5) || "09:00", endTime: ex?.end_time?.slice(0,5) || "17:00" };
        }));
      }

      setLoading(false);
    };

    load();
  }, [navigate]);

  // ── Validate current tab ───────────────────────────────────
  const validate = () => {
    const e = {};
    if (activeTab === 0) {
      if (!bio.trim() || bio.trim().length < 80) e.bio   = "Bio must be at least 80 characters";
      if (!photoPreview && !photoFile)            e.photo = "Please upload a profile photo";
    }
    if (activeTab === 1) {
      if (!expertise.trim()) e.expertise = "Required";
    }
    if (activeTab === 3) {
      if (bank.accountNumber && bank.accountNumber !== bank.confirmAccount)
        e.confirmAccount = "Account numbers do not match";
      if (bank.ifsc && !bank.ifsc.match(/^[A-Z]{4}0[A-Z0-9]{6}$/))
        e.ifsc = "Invalid IFSC (e.g. SBIN0001234)";
    }
    setErrs(e);
    return Object.keys(e).length === 0;
  };

  // ── Photo select ───────────────────────────────────────────
  const handlePhotoSelect = (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { setAlert({ type:"error", msg:"Photo must be under 3MB" }); return; }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setErrs(p => ({ ...p, photo: "" }));
  };

  // ── Upload photo ───────────────────────────────────────────
  // ── Upload photo to Supabase Storage ─────────────────────
  // Profile photos go to mentor-photos (PUBLIC bucket) so they
  // load correctly in <img> tags on mentor cards.
  // Documents (certificates) go to mentor-documents (PRIVATE).
  const uploadPhoto = async (mentorId) => {
    if (!photoFile) return photoPreview; // no new file — keep existing URL
    const ext  = photoFile.name.split(".").pop();
    const path = `profiles/${mentorId}/photo.${ext}`;

    const { data, error } = await supabase.storage
      .from("mentor-photos")           // ← PUBLIC bucket for photos only
      .upload(path, photoFile, { upsert: true, cacheControl: "3600" });

    if (error) throw new Error("Photo upload failed: " + error.message);

    const { data: urlData } = supabase.storage
      .from("mentor-photos")
      .getPublicUrl(data.path);

    return urlData.publicUrl;
  };

  // ── Save all ───────────────────────────────────────────────
  const handleSave = async () => {
    const allErrs = {};
    if (!bio.trim() || bio.trim().length < 80) allErrs.bio   = "Bio must be at least 80 characters";
    if (!photoPreview && !photoFile)           allErrs.photo = "Please upload a profile photo";
    if (!expertise.trim())                     allErrs.expertise = "Expertise is required";
    if (bank.accountNumber && bank.accountNumber !== bank.confirmAccount)
      allErrs.confirmAccount = "Account numbers do not match";
    if (bank.ifsc && !bank.ifsc.match(/^[A-Z]{4}0[A-Z0-9]{6}$/))
      allErrs.ifsc = "Invalid IFSC";

    if (Object.keys(allErrs).length) {
      setErrs(allErrs);
      setAlert({ type:"error", msg:"Please fix the errors before saving." });
      if (allErrs.bio || allErrs.photo)             setActiveTab(0);
      else if (allErrs.expertise)                   setActiveTab(1);
      else if (allErrs.confirmAccount || allErrs.ifsc) setActiveTab(3);
      return;
    }

    setSaving(true); setAlert(null);
    try {
      const photoUrl = await uploadPhoto(mentor.id);

      const { error: updateErr } = await supabase.from("mentors").update({
        bio:               bio.trim(),
        expertise:         expertise.trim(),
        category,
        session_price:     parseInt(sessionPrice, 10),
        languages:         languages.split(",").map(l => l.trim()).filter(Boolean),
        social_links:      { linkedin: socialLinkedIn, website: socialWebsite },
        profile_photo_url: photoUrl,
        bank_account_name:   bank.accountName   || null,
        bank_account_number: bank.accountNumber || null,
        bank_ifsc:           bank.ifsc ? bank.ifsc.toUpperCase() : null,
        is_active:  true,
        updated_at: new Date().toISOString(),
      }).eq("id", mentor.id);

      if (updateErr) throw updateErr;

      await supabase.from("mentor_availability").delete().eq("mentor_id", mentor.id);

      const toInsert = availability
        .filter(a => a.enabled)
        .map(a => ({ mentor_id: mentor.id, day_of_week: a.dayNum, start_time: `${a.startTime}:00`, end_time: `${a.endTime}:00` }));

      if (toInsert.length) await supabase.from("mentor_availability").insert(toInsert);

      setAlert({ type:"success", msg:"Profile saved! Redirecting to your dashboard…" });
      setTimeout(() => navigate("/mentordashboard", { replace: true }), 1500);
    } catch (err) {
      console.error("Save error:", err);
      setAlert({ type:"error", msg: err.message || "Save failed. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const sliderPct = ((sessionPrice - 150) / (400 - 150)) * 100;
  const upBank = (key, val) => setBank(p => ({ ...p, [key]: val }));

  if (loading) return (
    <>
      <style>{css}</style>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", flexDirection:"column", gap:12, fontFamily:"'DM Sans',sans-serif" }}>
        <div style={{ width:36, height:36, border:"3px solid #e8eaf6", borderTopColor:"#5c6bc0", borderRadius:"50%", animation:"mps-spin .6s linear infinite" }} />
        <span style={{ color:"#888" }}>Loading your profile…</span>
      </div>
    </>
  );

  return (
    <>
      <style>{css}</style>
      <div className="mps-root">
        <div className="mps-hero">
          <h1>Complete Your Mentor Profile</h1>
          <p>This is what users will see when browsing mentors</p>
        </div>

        <div className="mps-tabs">
          {TABS.map((label, i) => (
            <button key={i} className={`mps-tab ${activeTab === i ? "active" : ""}`}
              onClick={() => { if (validate()) { setActiveTab(i); setAlert(null); } }}>
              {["📸","🎯","📅","🏦"][i]} {label}
            </button>
          ))}
        </div>

        <div className="mps-card">
          <div className="mps-progress">
            {TABS.map((_, i) => <div key={i} className={`mps-dot ${i <= activeTab ? "done" : ""}`} />)}
          </div>

          {alert && (
            <div className={`mps-alert ${alert.type}`}>
              <span>{alert.type === "error" ? "⚠️" : alert.type === "success" ? "✅" : "ℹ️"}</span>
              {alert.msg}
            </div>
          )}

          {/* ── TAB 0: Photo & Bio ── */}
          {activeTab === 0 && (
            <>
              <div className="mps-card-title">Profile Photo & Bio</div>
              <div className="mps-card-sub">Your photo and bio are the first things users see.</div>
              <div className="mps-photo-wrap">
                {photoPreview
                  ? <img className="mps-photo-preview" src={photoPreview} alt="Profile" />
                  : <div className="mps-photo-placeholder">👤</div>
                }
                <div>
                  <div className="mps-photo-btn">
                    📷 {photoPreview ? "Change Photo" : "Upload Photo"}
                    <input type="file" accept="image/jpeg,image/png,image/webp"
                      onChange={e => handlePhotoSelect(e.target.files[0])} />
                  </div>
                  <div className="mps-hint" style={{ marginTop:6 }}>JPG, PNG, WEBP · Max 3MB</div>
                  {errs.photo && <div className="mps-err">⚠ {errs.photo}</div>}
                </div>
              </div>
              <div className="mps-field">
                <label className="mps-label">
                  About You / Bio <span className="req">*</span>
                  <span style={{ fontWeight:400, textTransform:"none", letterSpacing:0, color: bio.length < 80 ? "#f44336" : "#4caf50", marginLeft:8 }}>
                    {bio.length} / 80 min
                  </span>
                </label>
                <textarea className={`mps-textarea ${errs.bio ? "err" : ""}`} rows={6}
                  placeholder="Write a warm introduction. Describe your approach, your experience, and what users can expect."
                  value={bio} onChange={e => { setBio(e.target.value); setErrs(p => ({ ...p, bio:"" })); }} />
                {errs.bio && <div className="mps-err">⚠ {errs.bio}</div>}
              </div>
            </>
          )}

          {/* ── TAB 1: Expertise & Price ── */}
          {activeTab === 1 && (
            <>
              <div className="mps-card-title">Expertise & Pricing</div>
              <div className="mps-card-sub">Help users find you with the right keywords, and set a fair session price.</div>
              <div className="mps-grid">
                <div className="mps-field full">
                  <label className="mps-label">Mentoring Category <span className="req">*</span></label>
                  <div className="mps-cat-grid">
                    {CATEGORIES.map(c => (
                      <div key={c.value} className={`mps-cat-item ${category === c.value ? "selected" : ""}`}
                        onClick={() => setCategory(c.value)}>
                        <span className="mps-cat-icon">{c.icon}</span>
                        <span className="mps-cat-name">{c.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mps-field full">
                  <label className="mps-label">Key Expertise <span className="req">*</span></label>
                  <input className={`mps-input ${errs.expertise ? "err" : ""}`}
                    placeholder="e.g. Anxiety, CBT, Mindfulness (comma-separated)"
                    value={expertise} onChange={e => { setExpertise(e.target.value); setErrs(p => ({ ...p, expertise:"" })); }} />
                  {errs.expertise && <div className="mps-err">⚠ {errs.expertise}</div>}
                  <div className="mps-hint">These appear as tags on your mentor card.</div>
                </div>
                <div className="mps-field full">
                  <label className="mps-label">Session Price</label>
                  <div className="mps-price-display">₹{sessionPrice} <span>/ session (user pays ₹{sessionPrice + 10} with ₹10 platform fee)</span></div>
                  <input type="range" className="mps-slider" min="150" max="400" step="50"
                    style={{ "--val": `${sliderPct}%` }} value={sessionPrice}
                    onChange={e => setSessionPrice(parseInt(e.target.value))} />
                  <div className="mps-price-ticks"><span>₹150</span><span>₹200</span><span>₹250</span><span>₹300</span><span>₹350</span><span>₹400</span></div>
                  <div className="mps-hint" style={{ marginTop:6 }}>You earn ₹{Math.floor(sessionPrice * 0.65)}–₹{Math.floor(sessionPrice * 0.75)} depending on your rating tier.</div>
                </div>
                <div className="mps-field">
                  <label className="mps-label">Languages Spoken</label>
                  <input className="mps-input" placeholder="English, Hindi, Tamil"
                    value={languages} onChange={e => setLanguages(e.target.value)} />
                </div>
                <div className="mps-field">
                  <label className="mps-label">LinkedIn URL</label>
                  <input className="mps-input" placeholder="https://linkedin.com/in/yourname"
                    value={socialLinkedIn} onChange={e => setSocialLinkedIn(e.target.value)} />
                </div>
                <div className="mps-field">
                  <label className="mps-label">Website / Portfolio</label>
                  <input className="mps-input" placeholder="https://yourwebsite.com"
                    value={socialWebsite} onChange={e => setSocialWebsite(e.target.value)} />
                </div>
              </div>
            </>
          )}

          {/* ── TAB 2: Availability ── */}
          {activeTab === 2 && (
            <>
              <div className="mps-card-title">Weekly Availability</div>
              <div className="mps-card-sub">Set which days and time windows users can book sessions with you.</div>
              <div className="mps-avail-wrap">
                <table className="mps-avail-table">
                  <thead><tr><th>Day</th><th>Open</th><th>From</th><th>To</th></tr></thead>
                  <tbody>
                    {availability.map((a, i) => (
                      <tr key={a.day}>
                        <td style={{ fontWeight:600, fontSize:".85rem" }}>{a.day.slice(0,3)}</td>
                        <td><input type="checkbox" checked={a.enabled}
                          onChange={e => setAvailability(p => p.map((x,j) => j===i ? {...x, enabled:e.target.checked} : x))} /></td>
                        <td>
                          <select disabled={!a.enabled} value={a.startTime}
                            onChange={e => setAvailability(p => p.map((x,j) => j===i ? {...x, startTime:e.target.value} : x))}>
                            {TIMES.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </td>
                        <td>
                          <select disabled={!a.enabled} value={a.endTime}
                            onChange={e => setAvailability(p => p.map((x,j) => j===i ? {...x, endTime:e.target.value} : x))}>
                            {TIMES.filter(t => t > a.startTime).map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mps-alert info" style={{ marginTop:16 }}>
                <span>💡</span>
                <span>Users can book 1-hour slots within your available windows. You can update this anytime from your dashboard.</span>
              </div>
            </>
          )}

          {/* ── TAB 3: Bank Details ── */}
          {activeTab === 3 && (
            <>
              <div className="mps-card-title">Bank Details</div>
              <div className="mps-card-sub">Your earnings are transferred here after each confirmed session. Encrypted and never shown to users.</div>
              <div className="mps-grid">
                {[
                  { key:"accountName",    label:"Account Holder Name",  placeholder:"Exactly as on bank records", full:true },
                  { key:"bankName",       label:"Bank Name",             placeholder:"e.g. State Bank of India" },
                  { key:"accountNumber",  label:"Account Number",        placeholder:"9–18 digit number" },
                  { key:"confirmAccount", label:"Confirm Account No.",   placeholder:"Re-enter account number" },
                  { key:"ifsc",           label:"IFSC Code",             placeholder:"e.g. SBIN0001234" },
                ].map(({ key, label, placeholder, full }) => (
                  <div className={`mps-field ${full ? "full" : ""}`} key={key}>
                    <label className="mps-label">{label}</label>
                    <input className={`mps-input ${errs[key] ? "err" : ""}`}
                      placeholder={placeholder} value={bank[key]} autoComplete="off"
                      onChange={e => { upBank(key, key==="ifsc" ? e.target.value.toUpperCase() : e.target.value); setErrs(p => ({ ...p, [key]:"" })); }} />
                    {errs[key] && <div className="mps-err">⚠ {errs[key]}</div>}
                  </div>
                ))}
              </div>
              <div className="mps-alert info" style={{ marginTop:16 }}>
                <span>🔒</span>
                <span>Bank details are optional at this stage but required to receive payouts. You can add them later from dashboard settings too.</span>
              </div>
            </>
          )}

          {/* ── Navigation ── */}
          <div style={{ display:"flex", gap:10, marginTop:24 }}>
            {activeTab > 0 && (
              <button type="button" onClick={() => setActiveTab(t => t - 1)}
                style={{ padding:"12px 20px", background:"#f0f0f0", color:"#555", border:"none", borderRadius:10, fontWeight:600, cursor:"pointer" }}>
                ← Back
              </button>
            )}
            {activeTab < TABS.length - 1 ? (
              <button type="button" className="mps-save-btn" style={{ marginTop:0 }}
                onClick={() => { if (validate()) { setActiveTab(t => t + 1); setAlert(null); window.scrollTo({ top:0, behavior:"smooth" }); } }}>
                Next →
              </button>
            ) : (
              <button type="button" className="mps-save-btn" style={{ marginTop:0 }}
                onClick={handleSave} disabled={saving}>
                {saving ? <><span className="mps-spin" /> Saving profile…</> : "Save & Go to Dashboard 🚀"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}